import { Paper } from '@mui/material';
import type { RowData } from '@tanstack/react-table';
import type { DataTableViewSharedProps } from './dataTableView';
import { DataTableView } from './dataTableView';
import { type UseDataTableOptions, useDataTable } from './useDataTable';

export type {
  DataTableGetRowProps,
  DataTableViewSharedProps,
} from './dataTableView';

/**
 * You own the TanStack instance (e.g. from {@link useDataTable}). Do not pass `config`.
 */
export type DataTablePropsWithTable<TData extends RowData> =
  DataTableViewSharedProps<TData> & {
    config?: never;
  };

/**
 * {@link DataTable} runs {@link useDataTable} internally. Pass `config` only — do not pass `table`.
 */
export type DataTablePropsWithConfig<TData extends RowData> = Omit<
  DataTableViewSharedProps<TData>,
  'table'
> & {
  table?: never;
  config: UseDataTableOptions<TData>;
};

export type DataTableProps<TData extends RowData> =
  | DataTablePropsWithTable<TData>
  | DataTablePropsWithConfig<TData>;

function DataTableFromConfig<TData extends RowData>(
  props: DataTablePropsWithConfig<TData>,
): JSX.Element {
  const { config, ...viewProps } = props;
  const { table } = useDataTable(config);
  return (
    <Paper sx={{ width: '100%', overflow: 'visible' }}>
      <DataTableView {...viewProps} table={table} />
    </Paper>
  );
}

/**
 * Renders the design-system table for a TanStack instance produced by {@link useDataTable},
 * or accepts a {@link UseDataTableOptions} `config` object and wires the hook internally.
 *
 * Prefer `useDataTable` + `table` when you need the hook’s return values (sorting, filters, …)
 * in the same component; use `config` for simple read-only tables.
 */
export function DataTable<TData extends RowData>(
  props: DataTableProps<TData>,
): JSX.Element {
  if ('table' in props && props.table !== undefined) {
    return (
      <Paper sx={{ width: '100%', overflow: 'visible' }}>
        <DataTableView {...props} />
      </Paper>
    );
  }
  return <DataTableFromConfig {...props} />;
}
